import {
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SyncIcon,
  TrashIcon
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  IconButton,
  useConfirm
} from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";

import moment from "moment";
import { lichSuXuongPhongYTeApi } from "../../api/lichSuXuongPhongYTeApi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ILichSuXuongPhongYTe } from "../../models/response/crm-hocsinh/ILichSuXuongPhongYTe";
import HocSinhProfile from "../hoc-sinh-profile";
import ImportBHYTModal from "./import-bhyt-modal/ImportBHYTModal";
import LichSuXuongPhongYTeModal from "./LichSuXuongPhongYTeModal";
import TextTranslated from "../../components/text";
import HtmlView from "../../components/html-view";

const LichSuXuongPhongYTePage = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [lichSuXuongPhongYTes, setLichSuXuongPhongYTes] = useState<ILichSuXuongPhongYTe[]>([]);
  const { translate, checkAccesiableTo } = useCommonContext();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [isShowModalImport, setIsShowModalImport] = useState(false);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const handleReloadAsync = useCallback(async () => {

    try {
      setIsLoading(true);
      const res = await lichSuXuongPhongYTeApi.selectAll(dm_coso_id, nam_hoc);
      if (res.is_success) {
        setLichSuXuongPhongYTes(res.data);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  }, [nam_hoc, dm_coso_id]);

  useEffect(() => {
    handleReloadAsync();
  }, [nam_hoc, dm_coso_id, handleReloadAsync]);

  const handeDelete = async (id: number) => {
    if (
      await confirm({
        content: "Bạn có chắc chắn muốn xóa?",
        title: "Lưu ý",
        cancelButtonContent: "Không xóa",
        confirmButtonContent: "Xóa",
        confirmButtonType: "danger",
      })
    ) {
      try {
        const res = await lichSuXuongPhongYTeApi.delete(id);
        if (res.is_success) {
          NotifyHelper.Success("Success");
          handleReloadAsync();
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } catch (error) {
        NotifyHelper.Error("Có lỗi xảy ra khi xóa");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        height={`${window.innerHeight - 200}px`}
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={handleReloadAsync}
              disabled={isLoading}
            />
            <Button
              text={translate("Base.Label.Create")}
              leadingVisual={PlusIcon}
              variant="primary"
              size="medium"
              onClick={() => {
                setIsShowModal(true);
                setSelectedId(0);
              }}
              disabled={isLoading}
            />
          </Box>
        }
        columns={[
          {
            id: "cmd",
            width: "50px",
            caption: "#",
            align: "center",
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton
                      icon={KebabHorizontalIcon}
                      variant="invisible"
                      aria-label="Open column options"
                    />
                  </ActionMenu.Anchor>

                  <ActionMenu.Overlay>
                    <ActionList>

                      <ActionList.Item
                        onSelect={() => {
                          setIsShowModal(true);
                          if (data.id !== undefined) {
                            setSelectedId(data.id);
                          }
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <PencilIcon />
                        </ActionList.LeadingVisual>
                        <TextTranslated value="Base.Label.Edit" />
                      </ActionList.Item>

                      <ActionList.Item
                        variant="danger"
                        onSelect={() => {
                          if (data.id !== undefined) {
                            handeDelete(data.id);
                          }
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <TrashIcon />
                        </ActionList.LeadingVisual>
                        <TextTranslated value="Base.Label.Delete" />
                      </ActionList.Item>
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              );
            },
          },
          {
            dataField: "trang_thai",
            width: "150px",
            caption: `Trạng thái`,
            isMainColumn: true,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {data.trang_thai || ""}
                </Box>
              );
            },
          },
          {
            dataField: "ma_hs",
            width: "200px",
            caption: `Mã học sinh`,
          },
          {
            dataField: "ten_hoc_sinh",
            width: "250px",
            caption: `Tên học sinh`
          },
          {
            dataField: "tinh_trang",
            width: "200px",
            caption: `Tình trạng`,
          },
          {
            dataField: "ket_qua",
            width: "400px",
            caption: `Kết quả`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  <HtmlView value={data.ket_qua} />
                </Box>
              );
            },
          },
          {
            dataField: "ngay_tao",
            width: "150px",
            caption: `Ngày tạo`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {moment(data.ngay_tao).format("DD/MM/YYYY") || ""}
                </Box>
              );
            },
          },
          {
            dataField: "ngay_cap_nhat",
            width: "150px",
            caption: `Ngày sửa`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {moment(data.ngay_cap_nhat).format("DD/MM/YYYY") || ""}
                </Box>
              );
            },
          },
          {
            dataField: "nguoi_tao",
            width: "200px",
            caption: `Người tạo`,
          },
        ]}
        data={lichSuXuongPhongYTes}
        title="Lịc sử xuống phòng y tế"
        searchEnable
      />
      {isShowModal && (
        <LichSuXuongPhongYTeModal
          id={selectedId}
          onClose={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default LichSuXuongPhongYTePage;
